import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { logout, resetAllState } from "../../redux/Reducer/authSlice";
import Button from "../common/Button";
import {
  getStoreDetails,
  updateStatus,
} from "../../redux/Thunk/Store/StoreThunks";
import { persistor } from "../../redux/store";
import CheckedBg from "../common/icons/CheckedBg";
import Close from "../common/icons/Close";
import Timing from "../common/icons/Timing";
import Logout from "../common/icons/Logout";
import Inventory from "../common/icons/Inventory";
import { setNotification, setSidebarVisible } from "../../redux/Reducer/Store/StoreSlice";
import Dashbaord from "../common/icons/Dashboard";
import Receipt from "../common/icons/Receipt";
import ArrowBack from "../common/icons/ArrowBack";
import { setActiveTab } from "../../redux/Reducer/Admin/PartnerSlice";

const StoreSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [selectedRoute, setSelectedRoute] = useState("");
  const [isConfirm, setIsConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("CLOSED");
  const [notificationState, setNotificationState] = useState(null);
  const storeDetails = useSelector((state) => state?.store?.storeDetail?.store);
  const { role, user } = useSelector((state) => state.auth);
  const notificationPopup = useSelector((state) => state?.store?.notification);
  const isSidebarVisible = useSelector((state) => state.store?.isSidebarVisible);

  useEffect(() => {
    if (pathname) {
      setSelectedRoute(pathname);
    }
  }, [pathname]);

  useEffect(() => {
    if (!user?.token || role !== "store") {
      dispatch(resetAllState());
      localStorage.clear();
      persistor.purge();
      window.location.href = process.env.REACT_APP_STORE_URL;
    }
  }, [user, role, dispatch]);

  useEffect(() => {
    const status = storeDetails?.currentStoreStatus;
    setSelectedStatus(status);
  }, [storeDetails]);

  useEffect(() => {
    dispatch(getStoreDetails());
  }, [dispatch]);

  // Auto-hide Logic
  useEffect(() => {
    let timeoutId;
    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Wait 10 seconds of inactivity before hiding
      timeoutId = setTimeout(() => {
        // Only auto-hide if on common store pages and not interacting
        const storePaths = ["/store-dashboard", "/store-inventory", "/store-records"];
        if (storePaths.includes(pathname)) {
          dispatch(setSidebarVisible(false));
        }
      }, 5000); // define time in ms
    };

    const storePaths = ["/store-dashboard", "/store-inventory", "/store-records"];
    if (isSidebarVisible && storePaths.includes(pathname)) {
      resetTimer();
      const sidebarElement = document.getElementById("store-sidebar");
      if (sidebarElement) {
        sidebarElement.addEventListener("mousemove", resetTimer);
        sidebarElement.addEventListener("click", resetTimer);
      }

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (sidebarElement) {
          sidebarElement.removeEventListener("mousemove", resetTimer);
          sidebarElement.removeEventListener("click", resetTimer);
        }
      };
    }
  }, [isSidebarVisible, pathname, dispatch]);

  const handleLogout = () => {
    if (isConfirm) {
      const fcmToken = localStorage.getItem("fcmToken");
      const newStatus = { currentStoreStatus: "CLOSED", fcmToken };
      dispatch(updateStatus(newStatus));
      setTimeout(() => {
        dispatch(logout());
        dispatch(resetAllState());
        localStorage.clear();
        persistor.purge();
        window.location.href = process.env.REACT_APP_STORE_URL;
      }, 300);
      setIsConfirm(false);
    } else {
      setIsConfirm(true);
    }
  };

  const handleToggleStatus = async () => {
    const STATUS_ORDER = ["ACTIVE", "AWAY", "CLOSED"];
    const currentIndex = STATUS_ORDER.indexOf(selectedStatus);
    const nextStatus = STATUS_ORDER[currentIndex >= 0 ? (currentIndex + 1) % STATUS_ORDER.length : 0];
    setSelectedStatus(nextStatus);
    const newStatus = { currentStoreStatus: nextStatus };
    const response = await dispatch(updateStatus(newStatus)).unwrap();
    if (response) {
      dispatch(getStoreDetails());
    }
  };

  useEffect(() => {
    if (notificationPopup?.title) {
      setNotificationState(notificationPopup);
    }
  }, [notificationPopup]);

  const handleNotification = () => {
    setNotificationState(null);
    dispatch(setNotification({}));
    dispatch(setActiveTab({ name: "Orders", index: 0 }));
    navigate("/store-dashboard");
  };

  return (
    <div
      id="store-sidebar"
      className={`fixed left-0 top-0 h-[100vh] w-[80px] z-[999] flex flex-col justify-between transition-all duration-500 ease-in-out ${isSidebarVisible ? "translate-x-0" : "-translate-x-full"
        } ${isConfirm ? " bg-primary-700" : "bg-primary-600"}`}
    >
      {/* Top Section */}
      <div className="flex flex-col relative">
        {notificationState && (
          <div className="absolute top-[80px] left-0 right-0 z-[1001] px-1 animate-bounce">
             <div onClick={handleNotification} className="w-full bg-[#f0fff4] border border-[#00c732] rounded-md p-1 shadow-xl cursor-pointer">
                <div className="flex flex-col items-center gap-1">
                   <img src="/logo.svg" alt="logo" className="h-[10px] w-auto" />
                   <div className="bg-primary-600 rounded-full p-1 shadow-sm">
                      <img src="/notification.svg" alt="bell" className="h-[14px] w-[14px]" />
                   </div>
                   <span className="text-[9px] font-bold text-gray-800">#{notificationState?.orderId}</span>
                </div>
             </div>
          </div>
        )}
        {!isConfirm && (
          <ul className="flex flex-col">
            <li>
              <Link
                to={"/store-dashboard"}
                className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/store-dashboard"
                  ? "bg-primary-700 hover:text-primary-50/100"
                  : "hover:text-primary-50/30"
                  } font-medium`}
              >
                <Dashbaord />
                Dashboard
              </Link>
            </li>
            <li>
              <Link
                to={"/store-inventory"}
                className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/store-inventory"
                  ? "bg-primary-700 hover:text-primary-50/100"
                  : "hover:text-primary-50/30"
                  } font-medium`}
              >
                <Inventory />
                Inventory
              </Link>
            </li>
            <li>
              <Link
                to={"/store-records"}
                className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 ${selectedRoute === "/store-records"
                  ? "bg-primary-700 hover:text-primary-50/100"
                  : "hover:text-primary-50/30"
                  } font-medium`}
              >
                <div className="scale-110">
                  <Receipt height={20} width={20} />
                </div>
                Records
              </Link>
            </li>
          </ul>
        )}
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col pb-2">
        <div className="px-2 pb-2">
          <button
            className="w-full flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-primary-50 border border-white/80 rounded-md py-2 px-1 hover:bg-primary-700/50 active:scale-95 transition-all duration-200 cursor-pointer select-none"
            type="button"
            onClick={handleToggleStatus}
            title="Click to toggle status (Active -> Away -> Closed)"
          >
            {selectedStatus === "AWAY" ? (
              <Timing fill="#fff" />
            ) : selectedStatus === "ACTIVE" ? (
              <CheckedBg fill="#fff" />
            ) : selectedStatus === "CLOSED" ? (
              <Close fill="#fff" />
            ) : (
              <Close fill="#fff" />
            )}
            <span className="leading-tight">{selectedStatus || "CLOSED"}</span>
          </button>
        </div>

        <ul className="flex flex-col">
          {isConfirm && (
            <li>
              <Button
                className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 hover:text-primary-50/30 font-medium group`}
                onClick={() => setIsConfirm(false)}
              >
                <span className="transition-all duration-200 group-hover:opacity-20 scale-110">
                  <ArrowBack fill="#fff" height={25} width={25} />
                </span>
                Back
              </Button>
            </li>
          )}
          <li>
            <Button
              onClick={handleLogout}
              className={`flex flex-col items-center justify-center text-[12px] text-primary-50 gap-1 px-3 py-3 min-h-[64px] w-full transition-all duration-300 hover:text-primary-50/30 group ${isConfirm ? 'bg-red-500/10' : ''}`}
            >
              <span className={`transition-all duration-200 ${isConfirm ? "group-hover:opacity-20" : "group-hover:opacity-30"} scale-110`}>
                <Logout height={23} width={23} />
              </span>
              {isConfirm ? "Logout" : "Logout"}
            </Button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default StoreSidebar;
